import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getLeads, getStats, exportLeadsCSV, createManualLead } from '../../lib/api';
import { LEAD_STATUSES, LEAD_SOURCES, GYM_CONFIG, INTEREST_TYPES } from '../../config';
import { gvCall, gvText } from '../../utils/googleVoice';
import { formatPhone } from '../../utils/phone';
import KanbanBoard from '../../components/staff/KanbanBoard';
import {
  Search, Download, Plus, LogOut, RefreshCw, Phone,
  ChevronRight, Users, TrendingUp, Calendar, Activity,
  Loader2, LayoutGrid, List, ArrowLeft, ShieldOff
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

// Quick Add strip for walk-ins
function QuickAddStrip({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    interest_type: 'General Membership', lead_source: 'walk_in',
    location: 'santa_cruz', notes: '', training_goals: '', start_timeline: 'ASAP', preferred_contact: 'call'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.phone) { toast.error('First name and phone required'); return; }
    setLoading(true);
    try {
      await createManualLead(form);
      toast.success(`Added: ${form.first_name} ${form.last_name}`);
      setForm({ first_name: '', last_name: '', phone: '', email: '', interest_type: 'General Membership', lead_source: 'walk_in', location: 'santa_cruz', notes: '', training_goals: '', start_timeline: 'ASAP', preferred_contact: 'call' });
      setOpen(false);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = 'bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-500 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors duration-200';

  return (
    <div className="mb-4">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-50 border border-gray-200 hover:border-gray-200 px-4 py-2.5 rounded-lg w-full transition-colors duration-200"
        >
          <Zap size={14} className="text-[#FF2E00]" />
          <span className="font-medium">Quick Add</span>
          <span className="text-gray-500 text-xs ml-1">— walk-in, call-in, or any quick entry</span>
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="bg-[var(--surface)] border border-[#FF2E00]/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Zap size={14} className="text-[#FF2E00]" />
              <span className="text-gray-900 text-sm font-semibold">Quick Add Lead</span>
              <span className="text-gray-500 text-xs">walk-in / call-in</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} className="text-gray-600 hover:text-gray-900 text-xs px-2 py-1">
              Cancel
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
            <input
              required value={form.first_name}
              onChange={(e) => setForm(p => ({...p, first_name: e.target.value}))}
              placeholder="First Name *" className={inputClass}
            />
            <input
              value={form.last_name}
              onChange={(e) => setForm(p => ({...p, last_name: e.target.value}))}
              placeholder="Last Name" className={inputClass}
            />
            <input
              required value={form.phone}
              onChange={(e) => setForm(p => ({...p, phone: e.target.value}))}
              placeholder="Phone *" type="tel" className={inputClass}
            />
            <input
              value={form.email}
              onChange={(e) => setForm(p => ({...p, email: e.target.value}))}
              placeholder="Email (optional)" type="email" className={inputClass}
            />
          </div>
          <div className="flex gap-2">
            <input
              value={form.notes}
              onChange={(e) => setForm(p => ({...p, notes: e.target.value}))}
              placeholder="Quick note (optional)"
              className={`${inputClass} flex-1`}
            />
            <button
              type="submit" disabled={loading}
              className="btn-scs-primary px-4 py-2 rounded-md text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap"
            >
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Add
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const found = LEAD_STATUSES.find((s) => s.value === status);
  const colorClass = found?.color || 'bg-gray-100 text-gray-900 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${colorClass}`}>
      {found?.label || status}
    </span>
  );
}

const SOURCE_LABELS = {
  website_form: 'Website',
  book_a_tour: 'Tour Form',
  book_a_visit: 'Book Visit',
  contact_page: 'Contact',
  personal_training_inquiry: 'PT Inquiry',
  manual_entry: 'Manual',
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  // Redirect platform owners/admins to the HuscleOS Sales Dashboard.
  // Demo users (prospects walking through their sandbox) stay on this CRM.
  const shouldRedirect = user && !user.is_demo && (user.role === 'owner' || user.role === 'admin');
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [priorityFirst, setPriorityFirst] = useState(true); // website leads shown first by default
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('kanban');
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadLoading, setAddLeadLoading] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    interest_type: 'General Membership', training_goals: '',
    start_timeline: 'ASAP', preferred_contact: 'call', notes: '', location: 'santa_cruz',
  });

  const PRIORITY_SOURCES = LEAD_SOURCES.filter(s => s.priority);
  const OTHER_SOURCES    = LEAD_SOURCES.filter(s => !s.priority);

  const fetchData = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (sourceFilter !== 'all') params.lead_source = sourceFilter;
      if (priorityFirst && sourceFilter === 'all') params.priority_first = true;

      const [leadsRes, statsRes] = await Promise.all([getLeads({ ...params, limit: 200 }), getStats()]);
      setLeads(leadsRes.data.leads);
      setTotal(leadsRes.data.total);
      setStats(statsRes.data);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search, statusFilter, sourceFilter, priorityFirst]);

  useEffect(() => {
    const timer = setTimeout(() => fetchData(), search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchData, search]);

  const handleExport = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await exportLeadsCSV(params);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'scs-leads.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Leads exported');
    } catch { toast.error('Export failed'); }
  };

  const handleAddLead = async (e) => {
    e.preventDefault();
    setAddLeadLoading(true);
    try {
      await createManualLead(newLead);
      toast.success('Lead added');
      setAddLeadOpen(false);
      setNewLead({ first_name: '', last_name: '', email: '', phone: '', interest_type: 'General Membership', training_goals: '', start_timeline: 'ASAP', preferred_contact: 'call', notes: '', location: 'santa_cruz' });
      fetchData(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add lead');
    } finally { setAddLeadLoading(false); }
  };

  const KPI_STATS = [
    { label: 'New (7 days)', value: stats?.new_7d ?? '—', icon: <TrendingUp size={15} />, testid: 'crm-dashboard-kpi-new', color: 'text-gray-700' },
    { label: 'Tour Booked', value: stats?.by_status?.['Booked Visit'] ?? 0, icon: <Calendar size={15} />, testid: 'crm-dashboard-kpi-booked', color: 'text-[#FF8060]/50' },
    { label: 'Members', value: stats?.by_status?.['Joined'] ?? 0, icon: <Users size={15} />, testid: 'crm-dashboard-kpi-joined', color: 'text-[#FF2E00]/70' },
    { label: 'Total Leads', value: stats?.total ?? '—', icon: <Activity size={15} />, testid: 'crm-dashboard-kpi-total', color: 'text-gray-700' },
  ];

  const inputClass = 'bg-gray-50 border border-gray-200 text-gray-900 placeholder:text-gray-500 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-colors duration-200';

  // All hooks ran — now safe to redirect platform owners
  if (shouldRedirect) return <Navigate to="/staff/huscleos" replace />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gray-50 backdrop-blur border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="flex items-center gap-0 group" title="Back to HuscleOS">
              <span className="font-display tracking-wider text-sm" style={{ color: '#0F0F0F' }}>HUSCLE</span>
              <span className="font-display tracking-wider text-sm" style={{ color: '#FF2E00' }}>OS</span>
            </Link>
            <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase border" style={{ color: '#FF2E00', borderColor: 'rgba(255,46,0,0.4)' }} data-testid="demo-sandbox-badge">
              DEMO SANDBOX
            </span>
            <span className="text-gray-500 text-xs hidden md:block">— preview of your future HuscleOS CRM</span>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="flex items-center gap-1 text-gray-700 hover:text-gray-900 text-xs font-medium border border-gray-200 hover:border-gray-400 px-2.5 py-1.5 rounded-md transition-colors duration-200"
              data-testid="demo-exit-link"
            >
              <ArrowLeft size={11} />
              <span>Exit Demo</span>
            </Link>
            <span className="text-gray-700 text-xs hidden sm:block max-w-[120px] truncate">{user?.name}</span>
            <button onClick={() => { logout(); navigate('/staff/login'); }}
              className="text-gray-500 hover:text-gray-700 p-1.5 rounded transition-colors duration-200" title="Logout">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* KPI Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {KPI_STATS.map((kpi, i) => (
            <div key={i} className="bg-white border border-gray-200 p-4" data-testid={kpi.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-500 text-xs uppercase tracking-wider">{kpi.label}</span>
                <span className={kpi.color}>{kpi.icon}</span>
              </div>
              <span className="text-gray-900 text-2xl font-bold font-display tracking-wide">{kpi.value}</span>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col gap-3 mb-5">
          {/* Row 1: search + status + view toggle */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input type="text" placeholder="Search name, phone, email..."
                value={search} onChange={(e) => setSearch(e.target.value)}
                data-testid="crm-dashboard-search-input"
                className={`${inputClass} pl-9 w-full`} />
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="crm-dashboard-status-filter-select"
                className="w-full sm:w-44 bg-gray-50 border-gray-200 text-gray-900 text-sm h-9">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="all" className="text-gray-900 text-sm">All Statuses</SelectItem>
                {LEAD_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value} className="text-gray-900 text-sm">{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              {/* View toggle */}
              <div className="flex border border-gray-200 rounded-md overflow-hidden">
              <button
                onClick={() => setViewMode('kanban')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors duration-200 ${
                  viewMode === 'kanban' ? 'bg-[#FF2E00] text-gray-900' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Kanban view"
              >
                <LayoutGrid size={14} />
                <span className="hidden sm:block text-xs">Board</span>
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 text-sm flex items-center gap-1.5 transition-colors duration-200 ${
                  viewMode === 'table' ? 'bg-[#FF2E00] text-gray-900' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                }`}
                title="Table view"
              >
                <List size={14} />
                <span className="hidden sm:block text-xs">List</span>
              </button>
            </div>

            <button onClick={() => fetchData(true)}
              className="btn-scs-secondary px-3 py-2 rounded-md text-sm flex items-center gap-1.5"
              title="Refresh">
              <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleExport}
              className="btn-scs-secondary px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
              <Download size={13} />
            </button>
            <button onClick={() => setAddLeadOpen(true)}
              className="btn-scs-primary px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
              <Plus size={13} />
              <span className="hidden sm:block text-xs">Add Lead</span>
            </button>
          </div>
          </div>{/* close Row 1 flex */}

          {/* Row 2: Source quick-filter pills */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-[10px] font-bold uppercase tracking-widest shrink-0">Source:</span>

            {/* All — with priority toggle */}
            <button
              onClick={() => { setSourceFilter('all'); }}
              className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                sourceFilter === 'all'
                  ? 'bg-white text-[#0f1a14] border-white'
                  : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-gray-400'
              }`}>
              All
            </button>

            {/* Priority sources (website, walk-in, etc.) — highlighted in green */}
            {PRIORITY_SOURCES.map(s => (
              <button key={s.value}
                onClick={() => setSourceFilter(sourceFilter === s.value ? 'all' : s.value)}
                className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                  sourceFilter === s.value
                    ? 'bg-[#FF2E00] text-gray-900 border-[#FF2E00]'
                    : 'bg-[#FF2E00]/10 text-[#FF8060] border-[#FF2E00]/25 hover:bg-[#FF2E00]/20'
                }`}
                data-testid={`source-pill-${s.value}`}>
                {s.label}
              </button>
            ))}

            {/* CSV / Manual — dimmer */}
            {OTHER_SOURCES.map(s => (
              <button key={s.value}
                onClick={() => setSourceFilter(sourceFilter === s.value ? 'all' : s.value)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                  sourceFilter === s.value
                    ? 'bg-gray-50 text-gray-900 border-gray-300'
                    : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-400 hover:text-gray-900/50'
                }`}
                data-testid={`source-pill-${s.value}`}>
                {s.label}
              </button>
            ))}

            {/* Priority sort toggle */}
            {sourceFilter === 'all' && (
              <button
                onClick={() => setPriorityFirst(v => !v)}
                className={`ml-2 flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-full border transition-all duration-150 ${
                  priorityFirst
                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                    : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-400'
                }`}
                title="When on, website/walk-in leads appear before imported leads">
                {priorityFirst ? '⭐ Website First' : '⭐ Website First (off)'}
              </button>
            )}
          </div>
        </div>{/* close outer flex flex-col */}

        {/* Quick Add strip for walk-ins */}
        <QuickAddStrip onAdded={() => fetchData(true)} />

        {/* Lead count */}
        <div className="mb-4">
          <p className="text-gray-600 text-xs">
            {loading ? 'Loading...' : `${total} lead${total !== 1 ? 's' : ''}${
            search || statusFilter !== 'all' || sourceFilter !== 'all'
              ? ' (filtered)'
              : priorityFirst ? ' · website leads first' : ''
          }`}
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-2 border-[#FF2E00] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="bg-white border border-gray-200 text-center py-20">
            <Users size={32} className="text-gray-500 mx-auto mb-3" />
            <p className="text-gray-600 text-sm">No leads found</p>
            <p className="text-gray-500 text-xs mt-1">Adjust your filters or add a new lead</p>
          </div>
        ) : viewMode === 'kanban' ? (
          <KanbanBoard leads={leads} onLeadsUpdated={() => fetchData(true)} />
        ) : (
          // TABLE VIEW
          <div className="bg-white border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full crm-table" data-testid="crm-dashboard-leads-table">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left hidden sm:table-cell">Phone</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left hidden md:table-cell">Source</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Interest</th>
                    <th className="px-4 py-3 text-left hidden lg:table-cell">Date</th>
                    <th className="px-4 py-3 text-left"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}
                      className="border-t border-gray-100 cursor-pointer transition-colors duration-150"
                      onClick={() => navigate(`/demo/leads/${lead.id}`)}>
                      <td className="px-4 py-3">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="text-gray-900 text-sm font-medium">{lead.first_name} {lead.last_name}</p>
                            {lead.blacklisted && <ShieldOff size={11} className="text-red-400/70" title="Blacklisted" />}
                          </div>
                          <p className="text-gray-600 text-xs">{lead.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                          <button onClick={() => gvCall(lead.phone)}
                            title="Call via Google Voice"
                            className="text-gray-500 hover:text-[#FF8060] text-xs flex items-center gap-1 transition-colors duration-150">
                            <Phone size={11} />{formatPhone(lead.phone)}
                          </button>
                          {lead.phone && (
                            <button onClick={() => gvText(lead.phone)}
                              title="Text via Google Voice"
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-purple-500/15 border border-purple-500/20 text-purple-300 hover:bg-purple-500/25 transition-colors duration-150 shrink-0">
                              Txt
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={lead.status} /></td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-gray-700 text-xs">{SOURCE_LABELS[lead.lead_source] || lead.lead_source}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-700 text-xs">{lead.interest_type}</span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="text-gray-500 text-xs">
                          {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight size={13} className="text-gray-500" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add Lead Dialog */}
      <Dialog open={addLeadOpen} onOpenChange={setAddLeadOpen}>
        <DialogContent className="bg-[var(--surface)] border-gray-200 text-gray-900 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide text-lg">ADD LEAD</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLead} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">First Name *</label>
                <input required value={newLead.first_name} onChange={(e) => setNewLead(p => ({...p, first_name: e.target.value}))}
                  className={inputClass + ' w-full'} placeholder="Alex" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Last Name *</label>
                <input required value={newLead.last_name} onChange={(e) => setNewLead(p => ({...p, last_name: e.target.value}))}
                  className={inputClass + ' w-full'} placeholder="Smith" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Phone *</label>
              <input required type="tel" value={newLead.phone} onChange={(e) => setNewLead(p => ({...p, phone: e.target.value}))}
                className={inputClass + ' w-full'} placeholder="(831) 555-0100" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Email *</label>
              <input required type="email" value={newLead.email} onChange={(e) => setNewLead(p => ({...p, email: e.target.value}))}
                className={inputClass + ' w-full'} placeholder="alex@example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Interest</label>
              <select value={newLead.interest_type} onChange={(e) => setNewLead(p => ({...p, interest_type: e.target.value}))}
                className={inputClass + ' w-full appearance-none'} style={{backgroundColor:'#FFFFFF'}}>
                {INTEREST_TYPES.map(t => <option key={t} value={t} style={{background:'#1A1A1A'}}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Notes</label>
              <textarea value={newLead.notes} onChange={(e) => setNewLead(p => ({...p, notes: e.target.value}))}
                className={inputClass + ' w-full resize-none'} rows={2} placeholder="Any notes..." />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setAddLeadOpen(false)} className="flex-1 btn-scs-secondary py-2.5 rounded-md text-sm">Cancel</button>
              <button type="submit" disabled={addLeadLoading} className="flex-1 btn-scs-primary py-2.5 rounded-md text-sm flex items-center justify-center gap-2">
                {addLeadLoading ? <><Loader2 size={13} className="animate-spin" /> Adding...</> : 'Add Lead'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
